import { Info } from "@mui/icons-material";
import { Box, Slider, Typography } from "@mui/material";
import { useCallback } from "react";
import { formatPrice, PriceInput } from "@/components/common/PriceInput";

import { useFieldContext } from "./form.context";

type Props = {
  min: number;
  max: number;
  label?: string;
  driverSelected?: boolean;
};

export function PricingSliderField({ min, max, label, driverSelected = false }: Props) {
  const field = useFieldContext<number>();
  const value = field.state.value ?? 0;
  const isRangeValid = min < max;

  const handleChange = useCallback(
    (next: number) => {
      const clamped = Math.max(min, Math.min(max, next));
      field.handleChange(clamped);
    },
    [field, min, max]
  );

  const handleBlur = useCallback(() => {
    field.handleBlur();
  }, [field]);

  const handleSliderChange = useCallback(
    (_: Event, v: number | number[]) => {
      handleChange(Array.isArray(v) ? (v[0] ?? min) : v);
    },
    [handleChange, min]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.95) 100%)",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: "0.9rem",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "text.primary",
          }}
        >
          <Info sx={{ fontSize: 18 }} />
          {label ?? "Prix recommandé"}
          {driverSelected ? " pour ce chauffeur" : ""}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            background: "linear-gradient(135deg, #00244f 0%, #1A5DA6 100%)",
            px: 2,
            py: 0.75,
            borderRadius: 2,
            minWidth: 120,
            boxShadow: "0 2px 8px rgba(0, 36, 79, 0.2)",
          }}
        >
          <PriceInput
            value={value}
            onChange={handleChange}
            min={min}
            max={max}
            step={0.01}
            suffix="€"
            disabled={!isRangeValid}
            variant="standard"
            size="small"
            fullWidth={false}
            onBlur={handleBlur}
            sx={{
              "& .MuiInput-root": { "&::before, &::after": { display: "none" } },
              "& input": {
                background: "transparent",
                border: "none",
                outline: "none",
                color: "#fff",
                fontSize: "0.9rem",
                fontWeight: "bold",
                textAlign: "center",
                padding: 0.5,
              },
            }}
          />
        </Box>
      </Box>

      <Slider
        value={value}
        onChange={handleSliderChange}
        min={min}
        max={max}
        step={0.01}
        disabled={!isRangeValid}
        valueLabelDisplay="auto"
        valueLabelFormat={(v) => `${formatPrice(Number(v)) || "0.00"} €`}
        sx={{
          mt: 2,
          "& .MuiSlider-rail": {
            opacity: 0.35,
            height: 8,
            borderRadius: 4,
          },
          "& .MuiSlider-track": {
            height: 8,
            borderRadius: 4,
          },
          "& .MuiSlider-thumb": {
            width: 20,
            height: 20,
            boxShadow: "0 2px 12px rgba(25, 118, 210, 0.35)",
            "&:hover, &.Mui-focusVisible": {
              boxShadow: "0 0 0 8px rgba(25, 118, 210, 0.16)",
            },
          },
        }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          Min : {formatPrice(min) || "0.00"} €
        </Typography>
        <Typography variant="caption" fontWeight={600} color="text.secondary">
          Max : {formatPrice(max) || "0.00"} €
        </Typography>
      </Box>
    </>
  );
}
